import { useMutation } from '@tanstack/react-query';
import { Resource } from '@domain/base/Resource';
import { FullSyncJob } from '@domain/connection/models/ImportJob';
import { useImportJobsStore } from '../../../../application/store/importJobsStore';
import { useToast } from '@shared/hooks/useToast';

interface TriggerFullSyncArgs {
    connectionId: string;
    connectionName: string;
    syncFn: (connectionId: string) => Promise<Resource<FullSyncJob[]>>;
}

const ORDER_QUALIFIERS = ['new', 'all'];

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

/** Turns a SyncTarget slug (e.g. 'orders-new', 'search-terms') into a readable label. */
const formatSyncTarget = (target: string): string => {
    const parts = target.split('-');
    if (parts.length === 2 && ORDER_QUALIFIERS.includes(parts[1])) {
        return `${capitalize(parts[1])} ${capitalize(parts[0])}`;
    }
    return parts.map(capitalize).join(' ');
};

/** Shared "full sync" trigger: enqueues every sync target for a connection and tracks the resulting jobs (labeled by sync type) in the global import-jobs widget. */
export const useFullSync = (connector: string) => {
    const addJob = useImportJobsStore((s) => s.addJob);
    const { success, error: toastError } = useToast();

    const { mutateAsync: triggerFullSync, isLoading: isSyncing } = useMutation({
        mutationFn: async ({ connectionId, connectionName, syncFn }: TriggerFullSyncArgs) => {
            const result = await syncFn(connectionId);
            if (result.status === 'error') throw new Error(result.error.message);

            result.data.forEach((job) => {
                addJob({ id: job.jobId, connector, label: `${connectionName} · ${formatSyncTarget(job.target)}` });
            });

            return result.data;
        },
        onSuccess: () => success('Full sync started'),
        onError: (e) => toastError(e instanceof Error ? e.message : 'Failed to start full sync'),
    });

    return { triggerFullSync, isSyncing };
};
